import { LAYOUT_ALGORITHM, layoutAlgorithmLocale } from "Common/dictionaries/layoutAlgorithm";
import { formatFractionToPercent } from "Common/digitUtils/format";
import { ButtonWithTooltip, RedoButton, SaveButton, UndoButton } from "Components/button";
import { TextWidget } from "Components/optionForm";
import ScrolledDropdown from "Components/scrolledDropdown";
import classNames from "classnames";
import CheckBox from "devextreme-react/check-box";
import debounce from "lodash/debounce";
import find from "lodash/find";
import head from "lodash/head";
import map from "lodash/map";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ZOOM, redoButtonAttrs, undoButtonAttrs } from "../constants";
import locale from "./rowActionsPanel.locale";
import Styled, { SIZES } from "./rowActionsPanel.styled";
import Button from "Components/button/button";
import NodeCreatorPanel from "Areas/Settings/ontologicalModel/nodeCreatorPanel/nodeCreatorPanel";
import { IWithTooltip } from "Components/withPopover";
import ModelErrorsPanel from "Areas/Settings/ontologicalModel/modelErrorsPanel/modelErrorsPanel";
const keySelector = (e) => e.key;
const textSelector = (e) => e.text;
const zoomValues = [ZOOM.DEFAULT];
const step = 0.25;
while (zoomValues[zoomValues.length - 1] + step < ZOOM.MAX)
    zoomValues.push(zoomValues[zoomValues.length - 1] + step);
zoomValues.push(ZOOM.MAX);
while (zoomValues[0] - step > ZOOM.MIN)
    zoomValues.unshift(zoomValues[0] - step);
zoomValues.unshift(ZOOM.MIN);
const zoomItems = map(zoomValues, item => ({
    key: item,
    text: formatFractionToPercent(item, 1)
}));
const layoutItems = map([LAYOUT_ALGORITHM.HIERARCHIC, LAYOUT_ALGORITHM.ORGANIC, LAYOUT_ALGORITHM.CIRCULAR], item => ({
    key: item,
    text: layoutAlgorithmLocale[item]
}));
function RowActionsPanel(props) {
    const [filterText, setFilterText] = useState(props.filterText || "");
    const [nodeCreatorOpened, setNodeCreatorOpened] = useState(false);
    const [modelErrorsOpened, setModelErrorsOpened] = useState(false);
    const filterChangedCallback = useCallback(debounce((text) => {
        props.onChangeFilterText && props.onChangeFilterText(text);
    }, 400), [props.onChangeFilterText]);
    useEffect(() => {
        filterChangedCallback(filterText);
    }, [filterText]);
    const layoutAlgorithmTogglerContent = useMemo(() => {
        var _a;
        const layoutAlgorithmText = (_a = find(layoutItems, ["key", props.layoutAlgorithm])) === null || _a === void 0 ? void 0 : _a.text;
        return (React.createElement("span", { className: "grid-cols-auto no-gap" },
            React.createElement("i", { className: "icon-layout-algorithm font-h5" }),
            layoutAlgorithmText && React.createElement("span", { className: "ml-0_5 font-small" }, layoutAlgorithmText)));
    }, [props.layoutAlgorithm]);
    const handleShowNamesChanged = useCallback(() => {
        props.onSetShowNames && props.onSetShowNames(!props.showNames);
    }, [props.showNames, props.onSetShowNames]);
    const handleChooseImportFile = useCallback((evt) => {
        const input = evt.element.querySelector("input[type='file']");
        input && input.click();
    }, []);
    const handleFileChange = useCallback((evt) => {
        const file = head(evt.target.files);
        file && props.onUpload && props.onUpload(file);
        evt.target.value = "";
    }, []);
    const handleNodeCreatorOpen = useCallback(() => {
        setNodeCreatorOpened(true);
    }, []);
    const handleNodeCreatorClose = useCallback(() => {
        setNodeCreatorOpened(false);
    }, []);
    const handleModelErrorsOpen = useCallback(() => {
        setModelErrorsOpened(true);
    }, []);
    const handleModelErrorsClose = useCallback(() => {
        setModelErrorsOpened(false);
    }, []);
    return (React.createElement(Styled.ActionsMiniPanel, { className: classNames("bg-light-surface", props.className), hasRightSidePanel: Boolean(props.rightSidePanel), panelHeight: props.rowActionsPanelHeight },
        React.createElement("div", { className: "grid-cols-auto no-gap overflow-hidden" },
            React.createElement("div", { className: "p-0-8-12" }, props.sourceModelWasApplied &&
                (props.canEdit || (props.modelErrors && Boolean(props.modelErrors.length > 0))) && (React.createElement("div", { className: "flex-box-centered-content with-gap-5 " },
                props.canEdit && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { height: 36, className: "btn-primary font-small pt-0 pb-0", onClick: handleNodeCreatorOpen, disabled: Boolean(filterText) },
                        React.createElement("i", { className: "icon-add font-h5 mr-0_25" }),
                        locale.ADD_DOCUMENT_OBJECT),
                    React.createElement(NodeCreatorPanel, { open: nodeCreatorOpened, onClose: handleNodeCreatorClose, onAdd: props.handleDocumentObjectAdd }))),
                props.modelErrors && Boolean(props.modelErrors.length > 0) && (React.createElement(React.Fragment, null,
                    React.createElement(IWithTooltip, { className: "icon-info-circle-filled text-danger-400 font-h4 line-height-1 clickable animate-hover-color-0_8", onClick: handleModelErrorsOpen, popoverContent: locale.SHOW_MODEL_ERRORS }),
                    React.createElement(ModelErrorsPanel, { errors: props.modelErrors, open: modelErrorsOpened, onClose: handleModelErrorsClose })))))),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement(SaveButton, { className: "btn-arrow-action", onSave: props.onSave, disabled: !props.onSave, btnSize: SIZES.BTN_SIZE }),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement(UndoButton, { disabled: !props.canUndoOrRedo, elementAttr: undoButtonAttrs, btnSize: SIZES.BTN_SIZE, className: "btn-arrow-action" }),
            React.createElement(RedoButton, { disabled: !props.canUndoOrRedo, elementAttr: redoButtonAttrs, btnSize: SIZES.BTN_SIZE, className: "btn-arrow-action" }),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement(ButtonWithTooltip, { height: SIZES.BTN_SIZE, width: SIZES.BTN_SIZE, onClick: props.onDelete, disabled: !props.onDelete, popoverContent: locale.DELETE, className: "btn-arrow-action no-border" },
                React.createElement("i", { className: "icon-trash font-h5" })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement(ButtonWithTooltip, { height: SIZES.BTN_SIZE, width: SIZES.BTN_SIZE, className: "btn-arrow-action no-border", onClick: props.onDownload, disabled: !props.onDownload, popoverContent: locale.DOWNLOAD },
                React.createElement("i", { className: "icon-import font-h5" })),
            React.createElement(ButtonWithTooltip, { height: SIZES.BTN_SIZE, width: SIZES.BTN_SIZE, className: "btn-arrow-action no-border", onClick: handleChooseImportFile, disabled: !props.onUpload, popoverContent: locale.UPLOAD },
                React.createElement("i", { className: "icon-import font-h5 rotate-180" }),
                React.createElement("input", { type: "file", onChange: handleFileChange, className: "dx-hidden" })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement(ButtonWithTooltip, { height: SIZES.BTN_SIZE, width: SIZES.BTN_SIZE, className: "btn-arrow-action no-border", onClick: props.onReloadOntology, disabled: !props.onReloadOntology, popoverContent: locale.RELOAD },
                React.createElement("i", { className: "icon-import-file font-h5" })),
            React.createElement(ButtonWithTooltip, { height: SIZES.BTN_SIZE, width: SIZES.BTN_SIZE, className: "btn-arrow-action no-border", onClick: props.onExportOntology, disabled: !props.onExportOntology, popoverContent: locale.EXPORT },
                React.createElement("i", { className: "icon-export-file font-h5" })),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement("div", { className: "pl-0_75 pr-0_25" },
                React.createElement(TextWidget, { mode: "search", value: filterText, stylingMode: "filled", height: 36, showClearButton: false, placeholder: locale.FILTER_TEXT_PLACEHOLDER, onChange: setFilterText })),
            React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: props.layoutAlgorithm, onSelectedChanged: props.onChangeLayoutAlgorithm, items: layoutItems, togglerBtnContent: layoutAlgorithmTogglerContent, togglerClassName: "no-border bg-transparent btn-with-icon pt-0 pb-0", maxHeight: 300, minWidth: 90, disabled: Boolean(!props.onChangeLayoutAlgorithm || filterText) }),
            React.createElement("div", { className: "vertical-divider" }),
            React.createElement(CheckBox, { text: locale.SHOW_NAMES, className: "checkbox-small pl-0_75 nowrap", value: props.showNames, onValueChanged: handleShowNamesChanged, disabled: !props.onSetShowNames })),
        React.createElement("div", { className: "grid-cols-auto with-gap-xs overflow-hidden pl-0_5" },
            React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: props.zoom, onSelectedChanged: props.onChangeZoom, items: zoomItems, togglerClassName: "no-border bg-transparent p-0", togglerHeight: SIZES.BTN_SIZE, minWidth: 75, maxHeight: 350, caption: formatFractionToPercent(props.zoom, 1), togglerTitle: locale.ZOOM, togglerMuteDefaultCaptionText: false })),
        props.rightSidePanel));
}
export default memo(RowActionsPanel);
